/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react'
import { useItemsQuery } from '@/hooks/useItems'
import Loading from '@/components/Loading'
import RubanCategory from './RubanCategory'
import { useServerTableControls } from '@/hooks/useServerTableControls'
import type { ColumnDef } from '@tanstack/react-table'
import type { Item } from '@/types/backend'
import { DataTable } from '@/components/CustomTable'
import { useStore } from '@/store/Store'
import { currentQtyInCart, formatCFA, itemToCartItem } from '@/lib/utils'
import { toast } from 'sonner'
import clsx from 'clsx'
import { useCreateBillMutation } from '@/hooks/useBills'
import { useCustomerBalanceQuery } from '@/hooks/useCustomers'
import AppDialog from '@/components/CustomModal'
import { Button } from '@/components/ui/button'
import CartAside from '../cart/CartAside'

export default function CatalogDashboard() {
  // filtres locaux
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  )
  const {
    search,
    setSearch,
    debouncedSearch,
    pageIndex,
    pageSize,
    tableProps,
  } = useServerTableControls({ initialPageSize: 20 })
  const { state, dispatch } = useStore()
  const { mutateAsync: createBill } = useCreateBillMutation()
  const { data: customerBalance } = useCustomerBalanceQuery(
    state.cart.customerId ?? undefined,
    !!state.cart.customerId,
  )
  const [creditPromptOpen, setCreditPromptOpen] = useState(false)

  const {
    data: list,
    isLoading: itemsLoading,
    isFetching,
  } = useItemsQuery({
    search: debouncedSearch || undefined,
    categoryId: selectedCategoryId || undefined,
    page: pageIndex + 1,
    pageSize,
  })

  const total = list?.total ?? 0
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  )

  const qtyMap = useMemo(
    () => new Map(state.cart.items.map((it) => [it.itemId!, it.quantity])),
    [state.cart.items],
  )

  const stockById = useMemo(
    () =>
      new Map(
        (list?.data ?? []).map((it) => [
          it.id,
          it.type === 'SERVICE' ? Infinity : (it.stockOnHand ?? Infinity),
        ]),
      ),
    [list?.data],
  )

  const columns = useMemo<ColumnDef<Item, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <span className='font-medium'>{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        cell: ({ row }) => row.original.sku ?? '—',
      },
      {
        id: 'category',
        header: 'Catégorie',
        cell: ({ row }) => row.original.category?.name ?? '—',
      },
      {
        accessorKey: 'unitPrice',
        header: () => <div className='text-right'>Prix unitaire</div>,
        cell: ({ row }) => (
          <div className='text-right'>{formatCFA(row.original.unitPrice)}</div>
        ),
      },
      {
        accessorKey: 'stockOnHand',
        header: () => <div className='text-right'>Stock</div>,
        cell: ({ row }) => {
          if (row.original.type === 'SERVICE') {
            return <div className='text-right'>—</div>
          }
          const base = row.original.stockOnHand ?? 0
          const inCart = qtyMap.get(row.original.id) ?? 0
          const available = Math.max(0, base - inCart)
          const danger = available <= 5
          return (
            <div
              className={clsx(
                'text-right',
                danger && 'text-red-600 font-medium',
              )}
            >
              {available}
            </div>
          )
        },
      },
    ],
    [qtyMap],
  )

  const handleRowClick = (item: Item) => {
    const already = currentQtyInCart(state.cart, item.id)
    const nextQty = already + 1
    const available =
      item.type === 'SERVICE'
        ? Infinity
        : typeof item.stockOnHand === 'number'
          ? item.stockOnHand
          : Infinity

    if (nextQty > available) {
      toast.error('Stock insuffisant', {
        description: `Disponible: ${available}`,
      })
      return
    }
    dispatch({ type: 'CART_ADD_ITEM', payload: itemToCartItem(item, nextQty) })
    toast.success('Ajouté au panier')
  }

  const submitBill = async (applyExistingCredit: boolean) => {
    try {
      await createBill({ cart: state.cart, applyExistingCredit })
      dispatch({ type: 'CART_CLEAR' })
      toast.success('Facture créée')
    } catch (error: any) {
      toast.error('Erreur', {
        description:
          error?.response?.data?.message ?? 'Création facture échouée',
      })
    }
  }

  const handleValidate = async () => {
    const { cart } = state
    if (!cart.customerId) {
      toast.error('Client requis')
      return
    }
    if (cart.items.length === 0) {
      toast.error('Panier vide')
      return
    }
    if ((customerBalance?.availableCredit ?? 0) > 0) {
      setCreditPromptOpen(true)
      return
    }
    await submitBill(false)
  }

  return (
    <div className='grid gap-6 grid-cols-1 lg:grid-cols-[1fr_400px]'>
      {/* Colonne gauche : filtre + table */}
      <div className='space-y-6'>
        {/* FILTRES */}
        <div className='flex flex-col gap-4'>
          {/* Ruban catégories */}
          <RubanCategory
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
          />
        </div>

        {/* RÉSULTATS */}
        {itemsLoading ? (
          <Loading />
        ) : (
          <DataTable<Item, unknown>
            columns={columns}
            data={list?.data ?? []}
            {...tableProps}
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            rowClickable
            onRowClick={(row) => handleRowClick(row)}
            getRowClassName={(row) => {
              if (row.type === 'SERVICE') return ''
              const base = row.stockOnHand ?? 0
              const inCart = qtyMap.get(row.id) ?? 0
              const available = base - inCart
              return available <= 0
                ? 'opacity-60 cursor-not-allowed pointer-events-none'
                : ''
            }}
          />
        )}
        <div className='text-xs text-muted-foreground'>
          {isFetching ? 'Actualisation…' : null}
        </div>

        {/**Mobile: panier en dessous */}
        <div className='lg:hidden'>
          <CartAside stockById={stockById} onValidated={handleValidate} />
        </div>
      </div>
      {/* Colonne droite (desktop) : panier sticky */}
      <div className='hidden lg:block'>
        <CartAside stockById={stockById} onValidated={handleValidate} />
      </div>

      <AppDialog
        open={creditPromptOpen}
        onOpenChange={setCreditPromptOpen}
        title='Avoir disponible'
        description={`Ce client a ${formatCFA(
          customerBalance?.availableCredit ?? 0,
        )} en avoir. L'utiliser pour régler cette facture ?`}
        footer={
          <div className='flex w-full justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setCreditPromptOpen(false)
                void submitBill(false)
              }}
            >
              Ne pas utiliser
            </Button>
            <Button
              onClick={() => {
                setCreditPromptOpen(false)
                void submitBill(true)
              }}
            >
              Utiliser l'avoir
            </Button>
          </div>
        }
      />
    </div>
  )
}
